import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Chip, Divider } from "@mui/material";
import { isArray, isEmpty } from "lodash";
import { useState } from "react";
import { useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useChecklistRouteHook } from "../../hooks/useChecklistRouteHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { loanFetchedDataUwRequirementsSet } from "../../redux/reducers/loanFetchedDataReducer";
import CreateEditForm from "../finalForm/CreateEditForm";
import RffBasicHtmlEditor from "../finalForm/inputs/RffBasicHtmlEditor";
import RffBooleanToggleField from "../finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../finalForm/inputs/RffDateField";
import RffSelectDocGroupTypeField from "../finalForm/inputs/RffSelectDocGroupTypeField";
import RffSelectField from "../finalForm/inputs/RffSelectField";
import RffSelectToggleField from "../finalForm/inputs/RffSelectToggleField";
import RffSelectUserEmailField from "../finalForm/inputs/RffSelectUserEmailField";
import RffTextareaField from "../finalForm/inputs/RffTextareaField";
import RffGroup from "../finalForm/shared/RffGroup";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import RffBooleanSwitchField from "../finalForm/inputs/RffBooleanSwitchField";
import RffFormSection from "../finalForm/shared/RffFormSection";

const PRIORITY_OPTIONS = ["Low", "Medium", "High"];

const STATUS_OPTIONS = [
  "Open",
  "In Progress",
  "Accepted",
  "Rejected",
  "Second Level Review",
];

const DOC_STIP_STATUS_OPTIONS = [
  "Open Item",
  "Received - Pending Review",
  "Reviewed - Not Accepted",
  "Accepted",
];

const CreateUpdateLoanConditionForm = ({
  initialValues,
  onSuccessFn,
  onUpdateCancel,
  isUpdating,
}) => {
  const dispatch = useDispatch();

  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { clearFetchSetLoanDrilldown } = useReduxLoanDrilldownHook();

  const {
    loading,
    createLoanUnderwritingRequirement,
    createLoanUnderwritingRequirementWithFile,
    updateLoanUnderwritingRequirement,
  } = useChecklistRouteHook();

  const handleOnCreate = (values) => {
    const dataSpread = { ...values };
    // console.log("Before", dataSpread);
    if (dataSpread?.requirementType === "Document") {
      dataSpread.docStipStatus = "Open Item";
    }
    // console.log("After", dataSpread);
    // return;
    const requestParams = {
      dealId: loanDrilldown?._id,
      data: dataSpread,
      onSuccessFn: (response) => {
        dispatch(loanFetchedDataUwRequirementsSet(response));
        if (onSuccessFn) onSuccessFn(response);
      },
    };

    // console.log("requestParams", requestParams);
    // return;
    if (isEmpty(values?.uploadMetadata)) {
      createLoanUnderwritingRequirement(requestParams);
    } else {
      createLoanUnderwritingRequirementWithFile({
        dealId: loanDrilldown?._id,
        data: values,
        onSuccessFn: (response) => {
          clearFetchSetLoanDrilldown({ loanId: loanDrilldown?._id });
          if (onSuccessFn) onSuccessFn(response);
        },
      });
    }
  };

  const handleOnUpdate = (values) => {
    updateLoanUnderwritingRequirement({
      dealId: loanDrilldown?._id,
      requirementId: initialValues._id,
      data: values,
      onSuccessFn: (response) => {
        if (isArray(response)) {
          dispatch(loanFetchedDataUwRequirementsSet(response));
          return;
        }
        const resData = response?.conditions || response?.comments;
        dispatch(loanFetchedDataUwRequirementsSet(resData));
      },
    });
  };

  if (isUpdating) {
    return (
      <CreateEditForm
        onUpdate={handleOnUpdate}
        loading={loading}
        initialValues={initialValues}
        updateButtonText="Update Condition"
      >
        <StipFormQuestions editing />
      </CreateEditForm>
    );
  }
  return (
    <CreateEditForm
      onCreate={handleOnCreate}
      loading={loading}
      // formSpy
      initialValues={{ displayExternally: false }}
      createButtonText="Create Condition"
      // createButtonText="Create Requirement"
    >
      <StipFormQuestions />
    </CreateEditForm>
  );
};

/**
 * Form Fields
 */
const StipFormQuestions = ({ editing }) => {
  const { values } = useFormState();
  const isDocument = values?.requirementType === "Document";
  const [show, setShow] = useState(false);
  return (
    <Box
      sx={{
        ".rdw-editor-main": { minHeight: "80px" },
        ".MuiInputBase-root": { background: "#ffffff" },
      }}
    >
      {editing && (
        <>
          <Flx column g={1}>
            <Txt
              bold
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "1px",
                color: "#9497a6",
                marginBottom: "6px",
              }}
            >
              Status & Resolution
            </Txt>
            <RffFormSection suppressBottomMargin>
              <RffSelectToggleField
                name="status"
                label="Condition Status"
                options={STATUS_OPTIONS}
              />
              <RffSelectToggleField
                name="docStipStatus"
                label="Required Document Status"
                options={DOC_STIP_STATUS_OPTIONS}
              />

              <RffSelectUserEmailField
                name="resolvedBy"
                label="Resolved By"
                helperText="Email of the person responsible for resolving this condition"
              />
              <RffTextareaField
                name="resolutionNotes"
                label="Resolution Notes"
                minHeight={80}
              />
            </RffFormSection>
          </Flx>
          {/* <Divider sx={{ my: 3.5 }} /> */}
        </>
      )}
      <Flx column g={1}>
        {editing && (
          <Divider sx={{ my: 3 }}>
            <Chip
              size="small"
              variant="outlined"
              label={show ? "Show less details" : `Show all condition details`}
              icon={
                show ? (
                  <ExpandLess sx={{ fontSize: 16 }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 16 }} />
                )
              }
              onClick={() => setShow((prev) => !prev)}
              sx={{ height: 36, fontSize: "0.7rem", px: 2 }}
            />
          </Divider>
        )}

        {show || !editing ? (
          <>
            {/* <Flx fw jb ac>
              <Txt bold>Condition Details</Txt>
            </Flx> */}
            <RffGroup>
              <RffSelectToggleField
                required
                name="requirementType"
                label="Condition Type"
                options={["Condition", "Document", "Stip"]}
              />

              {isDocument && (
                <>
                  <RffSelectDocGroupTypeField
                    required
                    label="Required Document Type"
                  />
                </>
              )}
              <RffBasicHtmlEditor
                label="Requirement Details"
                placeholder="Start typing requirement details here..."
              />

              <RffDateField
                name="dueDateTime"
                label="Due Date"
                helperText="Optional deadline for when this condition should be satisfied"
              />

              <RffSelectField
                size={6}
                name="priority"
                placeholder="Select Priority"
                label="Priority Level"
                options={PRIORITY_OPTIONS}
              />
              <RffSelectUserEmailField
                size={6}
                name="assignedTo"
                label="Assigned To"
              />
              <RffBooleanSwitchField
                name="displayExternally"
                label="Share Externally"
                required
                description="If enabled, will be viewable by external users like a loan counterparty"
              />
            </RffGroup>
          </>
        ) : null}
      </Flx>
    </Box>
  );
};

export default CreateUpdateLoanConditionForm;
